import React, { useState } from 'react';
import { Prospect } from '../../shared/types';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { MapPin, Phone, Clock, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface LandingPageDemoProps {
  prospect: Prospect;
}

export const LandingPageDemo: React.FC<LandingPageDemoProps> = ({ prospect }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone) return;
    setFormSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans">
      {/* Top Banner Navigation back to Proposal */}
      <div className="bg-indigo-950/80 border-b border-indigo-500/30 py-2.5 px-4 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <Badge variant="indigo" className="uppercase font-bold">Modo Demonstração</Badge>
            <span className="hidden sm:inline text-indigo-200">
              Esta é uma simulação de Landing Page para <strong>{prospect.trade_name}</strong>
            </span>
          </div>
          <Link
            to={`/${prospect.slug}`}
            className="inline-flex items-center gap-1.5 font-bold text-indigo-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Proposta Comercial
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="py-6 border-b border-slate-800 bg-slate-900/60">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 font-black text-white flex items-center justify-center text-lg shadow-lg">
              {prospect.trade_name.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight text-white">{prospect.trade_name}</h1>
              <span className="text-xs text-slate-400">{prospect.segment} • {prospect.city}/{prospect.state}</span>
            </div>
          </div>

          <a href={`https://wa.me/${prospect.whatsapp}`} target="_blank" rel="noopener noreferrer">
            <Button variant="whatsapp" size="sm" icon={<Phone className="w-4 h-4" />}>
              Fale Conosco
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Badge variant="emerald" className="mb-4 uppercase tracking-wider">
            Excelência & Tradição em {prospect.city}
          </Badge>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-6">
            Soluções Especializadas em {prospect.segment} para a sua Empresa
          </h2>
          <p className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Atendimento personalizado, agilidade nos processos e suporte completo localizado em {prospect.city}/{prospect.state}.
          </p>

          {/* Form Lead Capture */}
          <Card className="max-w-xl mx-auto text-left border-indigo-500/30 bg-slate-900/90">
            <h3 className="text-lg font-bold text-white mb-1">Solicite um Orçamento Sem Compromisso</h3>
            <p className="text-xs text-slate-400 mb-4">Preencha os dados abaixo e entraremos em contato rapidamente.</p>

            {formSubmitted ? (
              <div className="p-6 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <h4 className="font-bold text-emerald-200 text-base">Solicitação Enviada com Sucesso!</h4>
                <p className="text-xs text-emerald-300/80 mt-1">
                  Nossa equipe em {prospect.city} entrará em contato pelo telefone {phone}.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Seu Nome Completo</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: João da Silva"
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">WhatsApp / Telefone</label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(00) 00000-0000"
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Como podemos ajudar?</label>
                  <textarea
                    rows={2}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Descreva brevemente sua necessidade..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <Button type="submit" variant="whatsapp" className="w-full" icon={<Send className="w-4 h-4" />}>
                  Enviar Solicitação via WhatsApp
                </Button>
              </form>
            )}
          </Card>
        </div>
      </section>

      {/* Info & Contact Details */}
      <section className="py-12 border-t border-slate-800 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-3 gap-6 text-sm">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-indigo-400 shrink-0 mt-1" />
            <div>
              <strong className="text-white block">Endereço</strong>
              <span className="text-slate-400 text-xs">{prospect.address || `${prospect.city}/${prospect.state}`}</span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-emerald-400 shrink-0 mt-1" />
            <div>
              <strong className="text-white block">Contato Direto</strong>
              <span className="text-slate-400 text-xs">{prospect.phone || prospect.whatsapp}</span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-purple-400 shrink-0 mt-1" />
            <div>
              <strong className="text-white block">Horário de Atendimento</strong>
              <span className="text-slate-400 text-xs">{prospect.business_hours || 'Segunda a Sexta: 08h às 18h'}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
